import { HeartPulse, Syringe, UserRound, Baby } from "lucide-react";

export const CARE_COORDINATION_STATEMENT =
  "Coordinated through qualified or licensed independent healthcare providers, subject to availability and clinical suitability.";

/**
 * Data for the reusable CareServicePage template. One object per nursing/care page.
 */
export const CARE_SERVICES = [
  {
    path: "/nursing-care-vietnam",
    title: "Nursing & Home Care",
    icon: HeartPulse,
    seoTitle: "Nursing Care in Vietnam | Hotel and Home Nurse Support",
    metaDescription:
      "Doctors Vietnam helps coordinate short-term nursing and practical home care for international patients, residents and families in Vietnam, at your hotel, home or apartment.",
    h1: "Nursing and Home Care Support in Vietnam",
    serviceType: "Nursing and home care coordination",
    intro:
      "Doctors Vietnam helps coordinate short-term nursing and practical care for international patients, residents and families in Vietnam.",
    listHeading: "Services may include",
    services: [
      "Nurse visit coordination",
      "Basic wellbeing checks",
      "Medication reminders based on prescribed instructions",
      "Mobility support",
      "Personal care assistance",
      "Hydration and meal support",
      "Communication with the treating doctor",
      "Follow-up appointment coordination",
      "Overnight or extended support where available",
    ],
    notes: {
      heading: "Please note",
      items: [
        "We do not promise 24-hour care unless it is confirmed for your individual case.",
        "Overnight or extended support is offered subject to availability.",
      ],
    },
  },
  {
    path: "/post-operative-nursing-vietnam",
    title: "Post-Operative Nursing",
    icon: Syringe,
    seoTitle: "Post-Operative Nursing Care in Vietnam | Recovery Support",
    metaDescription:
      "Arrange additional nursing and practical recovery support after surgery, dental treatment, cosmetic procedures or hospital discharge in Vietnam.",
    h1: "Post-Operative Nursing Support in Vietnam",
    serviceType: "Post-operative nursing coordination",
    intro:
      "Arrange additional nursing and practical recovery support after surgery, dental treatment, cosmetic procedures or hospital discharge.",
    listHeading: "Services may include",
    services: [
      "Recovery observation",
      "Medication reminders",
      "Wound and dressing support under doctor instructions",
      "Mobility assistance",
      "Personal care",
      "Hydration and meal coordination",
      "Doctor or clinic communication",
      "Follow-up appointments",
      "Transport to medical reviews",
      "Overnight support where available",
    ],
    notes: {
      heading: "Important",
      items: [
        "Nurses follow the treating doctor's instructions at all times.",
        "Doctors Vietnam does not change prescriptions or treatment plans.",
      ],
    },
  },
  {
    path: "/elderly-care-support-vietnam",
    title: "Elderly Care Support",
    icon: UserRound,
    seoTitle: "Elderly Care Support in Vietnam | Short-Term Home Assistance",
    metaDescription:
      "Doctors Vietnam helps families coordinate temporary support for elderly parents or relatives who need additional assistance at home or in a hotel in Vietnam.",
    h1: "Elderly Care and Temporary Support in Vietnam",
    serviceType: "Elderly care and temporary support coordination",
    intro:
      "We help families coordinate temporary support for elderly parents or relatives who need additional assistance in Vietnam.",
    listHeading: "Support may include",
    services: [
      "Daily wellbeing checks",
      "Medication reminders",
      "Assistance moving safely",
      "Help with meals and hydration",
      "Personal care support",
      "Companionship",
      "Appointment coordination",
      "Communication with family",
      "Doctor or nurse visit coordination",
      "Hospital discharge support",
    ],
    careTypes: [
      {
        tag: "Non-medical",
        title: "Non-medical carers",
        desc: "Companionship, personal care, help with meals, mobility and daily wellbeing. Non-medical carers are not nurses and do not provide clinical care.",
      },
      {
        tag: "Qualified",
        title: "Qualified nursing support",
        desc: "Licensed nurses for clinical needs such as medication administration and observation, arranged separately and subject to clinical suitability.",
        highlight: true,
      },
    ],
    notes: {
      heading: "Important",
      items: [
        "Companionship and personal-care workers are non-medical carers, not nurses.",
        "Qualified nursing support is arranged separately when clinical care is required.",
      ],
    },
  },
  {
    path: "/mother-baby-care-vietnam",
    title: "Mother & Baby Support",
    icon: Baby,
    seoTitle: "Mother and Baby Care Support in Vietnam | Postnatal Assistance",
    metaDescription:
      "Doctors Vietnam helps families coordinate temporary postnatal support after childbirth, or additional assistance with a newborn or young baby, in Vietnam.",
    h1: "Mother and Baby Support in Vietnam",
    serviceType: "Mother and baby (postnatal) support coordination",
    showEmergency: true,
    intro:
      "Doctors Vietnam helps families coordinate temporary support after childbirth or when additional assistance is needed with a newborn or young baby.",
    listHeading: "Support may include",
    services: [
      "Postnatal nurse or qualified caregiver coordination",
      "Support for the mother after hospital discharge",
      "Assistance with baby bathing and changing",
      "Feeding and routine support",
      "Sleep and wellbeing observations",
      "Help preparing bottles and baby supplies",
      "Appointment coordination",
      "Communication with paediatric or maternity providers",
      "Overnight support where available",
    ],
    notes: {
      heading: "Important",
      items: [
        "We do not provide unsupported medical advice.",
        "We cannot guarantee newborn nursing availability.",
        "The qualifications of the assigned nurse, midwife or caregiver are stated clearly before support begins.",
        "Emergency concerns involving a baby or mother must be referred to emergency medical services — call 115 in Vietnam or go to the nearest hospital emergency department.",
      ],
    },
  },
];

export const getCareService = (path) => CARE_SERVICES.find((s) => s.path === path);
